import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../lib/dateUtils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Shield, LogOut, Plus, User, KeyRound, Ban, RotateCcw, Search, Edit, Car } from 'lucide-react';
import { TransportSearch } from '../components/TransportSearch';
import { ITReviews } from '../components/reviews/ITReviews';
import { ITFinance } from '../components/finance/ITFinance';
import NotificationBell from '../components/NotificationBell';
import MobileActionMenu from '../components/MobileActionMenu';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ['it', 'master_admin', 'admin', 'transport', 'driver', 'parent'];

export default function ITDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', full_name: '', phone: '', role: 'master_admin' });

  const [deactivateUser, setDeactivateUser] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [showSearch, setShowSearch] = useState(false);

  const authHeader = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const params = {};
      if (filterRole !== 'all') params.role = filterRole;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (search.trim()) params.search = search.trim();
      const [uRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/it/users`, { ...authHeader(), params }),
        axios.get(`${API_URL}/it/stats`, authHeader()),
      ]);
      setUsers(uRes.data || []);
      setStats(sRes.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [authHeader, filterRole, filterStatus, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    if (!newUser.email || !newUser.password || !newUser.full_name || !newUser.role) {
      toast.error('Please fill in email, password, full name and role.');
      return;
    }
    try {
      await axios.post(`${API_URL}/it/users`, newUser, authHeader());
      toast.success('User created. Temporary password emailed to them.');
      setShowCreate(false);
      setNewUser({ email: '', password: '', full_name: '', phone: '', role: 'master_admin' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleForcePwd = async (u) => {
    try {
      await axios.put(`${API_URL}/it/users/${u.user_id}/force-password-change`, {}, authHeader());
      toast.success(`${u.full_name} will be asked to set a new password at next login.`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDeactivateConfirm = async () => {
    if (!deactivateUser) return;
    try {
      await axios.put(
        `${API_URL}/it/users/${deactivateUser.user_id}/deactivate`,
        { user_id: deactivateUser.user_id, reason: deactivateReason },
        authHeader(),
      );
      toast.success(`${deactivateUser.full_name} deactivated.`);
      setDeactivateUser(null);
      setDeactivateReason('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleEditSave = async () => {
    try {
      const { user_id, full_name, email, phone, role } = editUser;
      await axios.put(`${API_URL}/it/users/${user_id}`, { full_name, email, phone, role }, authHeader());
      toast.success('User updated.');
      setEditUser(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleReactivate = async (u) => {
    try {
      await axios.put(`${API_URL}/it/users/${u.user_id}/reactivate`, {}, authHeader());
      toast.success(`${u.full_name} reactivated.`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const roleColor = (r) => ({
    it: 'bg-slate-900 text-white',
    master_admin: 'bg-purple-600 text-white',
    admin: 'bg-indigo-600 text-white',
    transport: 'bg-sky-600 text-white',
    driver: 'bg-emerald-600 text-white',
    parent: 'bg-amber-600 text-white',
  }[r] || 'bg-gray-400 text-white');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white" data-testid="it-dashboard">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center shadow-sm">
              <Shield className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-heading font-bold text-foreground" data-testid="dashboard-title">IT Dashboard</h1>
              <p className="text-sm text-muted-foreground truncate" data-testid="user-name">Welcome, {user.full_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
          <NotificationBell />
          <MobileActionMenu title="IT Menu">
            <Button variant="default" onClick={() => setShowCreate(true)} className="rounded-full" data-testid="it-add-user-btn">
              <Plus className="w-4 h-4 mr-2" /> Add User
            </Button>
            <Button variant="outline" onClick={() => setShowSearch(true)} className="rounded-full" data-testid="it-search-transport-btn">
              <Car className="w-4 h-4 mr-2" /> Find Operator / Plate
            </Button>
            <Button variant="outline" onClick={() => setTimeout(() => document.querySelector('[data-testid="it-reviews-section"]')?.scrollIntoView({ behavior: 'smooth' }), 300)} className="rounded-full text-amber-700 border-amber-200" data-testid="it-reviews-btn">
              Reviews
            </Button>
            <Button variant="outline" onClick={() => setTimeout(() => document.querySelector('[data-testid="it-finance-section"]')?.scrollIntoView({ behavior: 'smooth' }), 300)} className="rounded-full text-emerald-700 border-emerald-200" data-testid="it-finance-btn">
              Finance Tracker
            </Button>
            <Button variant="outline" onClick={() => navigate('/profile')} className="rounded-full" data-testid="profile-button">
              <User className="w-4 h-4 mr-2" /> Profile
            </Button>
            <Button variant="outline" onClick={logout} className="rounded-full" data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </MobileActionMenu>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'bg-gray-50 text-gray-900' },
            { label: 'Active', value: stats.active, color: 'bg-emerald-50 text-emerald-700' },
            { label: 'Deactivated', value: stats.deactivated, color: 'bg-red-50 text-red-700' },
            { label: 'IT', value: stats.it, color: 'bg-slate-50 text-slate-800' },
            { label: 'Master Admins', value: stats.master_admin, color: 'bg-purple-50 text-purple-700' },
            { label: 'Admins', value: stats.admin, color: 'bg-indigo-50 text-indigo-700' },
            { label: 'Transport', value: stats.transport, color: 'bg-sky-50 text-sky-700' },
            { label: 'Drivers', value: stats.driver, color: 'bg-teal-50 text-teal-700' },
            { label: 'Parents', value: stats.parent, color: 'bg-amber-50 text-amber-700' },
          ].map((s, i) => (
            <Card key={i} className={`${s.color} shadow-sm`}>
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide opacity-75">{s.label}</p>
                <p className="text-2xl sm:text-3xl font-bold">{s.value ?? 0}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" placeholder="Name, email or phone" value={search}
                       onChange={(e) => setSearch(e.target.value)} data-testid="it-search-input" />
              </div>
            </div>
            <div className="w-full sm:w-auto">
              <Label className="text-xs">Role</Label>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="it-filter-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-auto">
              <Label className="text-xs">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="it-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="deactivated">Deactivated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* User list */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">All users ({users.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {users.length === 0 && <p className="text-sm text-muted-foreground">No users match the current filters.</p>}
            {users.map((u) => (
              <div key={u.user_id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 border rounded-xl bg-white" data-testid={`it-user-row-${u.user_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold truncate">{u.full_name}</p>
                    <Badge className={roleColor(u.role)}>{u.role.replace('_', ' ')}</Badge>
                    {u.status === 'deactivated'
                      ? <Badge variant="destructive">deactivated</Badge>
                      : <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">active</Badge>}
                    {u.force_password_change && <Badge variant="outline" className="border-amber-400 text-amber-700">must reset password</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                  {u.phone && <p className="text-xs text-muted-foreground">{u.phone}</p>}
                  {u.created_at && <p className="text-xs text-muted-foreground">Joined {formatDateTime(u.created_at)}</p>}
                </div>
                <div className="flex items-center gap-2 flex-wrap md:justify-end">
                  <Button variant="outline" size="sm" onClick={() => setEditUser({ ...u, phone: u.phone || '' })}
                          data-testid={`it-edit-${u.user_id}`} className="rounded-full">
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleForcePwd(u)}
                          data-testid={`it-force-pwd-${u.user_id}`} className="rounded-full">
                    <KeyRound className="w-4 h-4 mr-1" /> Force password reset
                  </Button>
                  {u.status === 'deactivated'
                    ? <Button variant="default" size="sm" onClick={() => handleReactivate(u)}
                              data-testid={`it-reactivate-${u.user_id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                        <RotateCcw className="w-4 h-4 mr-1" /> Reactivate
                      </Button>
                    : <Button variant="destructive" size="sm" onClick={() => { setDeactivateUser(u); setDeactivateReason(''); }}
                              data-testid={`it-deactivate-${u.user_id}`} className="rounded-full">
                        <Ban className="w-4 h-4 mr-1" /> Deactivate
                      </Button>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <ITReviews token={token} />
        <ITFinance token={token} />
      </main>

      {/* Create user dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="it-create-user-dialog">
          <DialogHeader>
            <DialogTitle>Add a new user</DialogTitle>
            <DialogDescription>IT can create any role, including Master Admins.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full name</Label>
              <Input value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })} data-testid="it-new-user-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="it-new-user-email" />
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} data-testid="it-new-user-phone" />
            </div>
            <div>
              <Label>Temporary password</Label>
              <Input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="it-new-user-password" />
              <p className="text-xs text-muted-foreground mt-1">The user will be forced to change this at next login.</p>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="it-new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="it-create-user-submit">Create user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TransportSearch open={showSearch} onOpenChange={setShowSearch} token={token} />

      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent data-testid="it-edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>Update this person's details or role.</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div>
                <Label>Full name</Label>
                <Input value={editUser.full_name} onChange={(e) => setEditUser({ ...editUser, full_name: e.target.value })} data-testid="it-edit-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} data-testid="it-edit-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={editUser.phone} onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })} data-testid="it-edit-phone" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={editUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })} disabled={editUser.user_id === user.user_id}>
                  <SelectTrigger data-testid="it-edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={handleEditSave} data-testid="it-edit-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate dialog */}
      <Dialog open={!!deactivateUser} onOpenChange={(o) => { if (!o) setDeactivateUser(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Deactivate account</DialogTitle>
            <DialogDescription>
              {deactivateUser?.full_name} will no longer be able to log in. You can reactivate them later.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason (optional)</Label>
            <Textarea value={deactivateReason} onChange={(e) => setDeactivateReason(e.target.value)} rows={3} data-testid="it-deactivate-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateUser(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeactivateConfirm} data-testid="it-confirm-deactivate">Deactivate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
